import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { Activity as ActivityIcon } from 'lucide-react';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await api.getActivityLogs(0, 100);
      setLogs(response.logs);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getActionBadge = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-gray-100 text-gray-700',
      staff_created: 'bg-blue-100 text-blue-700',
      staff_modified: 'bg-yellow-100 text-yellow-700',
      campaign_created: 'bg-purple-100 text-purple-700',
      campaign_modified: 'bg-orange-100 text-orange-700',
      password_reset: 'bg-red-100 text-red-700'
    };

    return (
      <Badge className={colors[action] || 'bg-slate-100 text-slate-700'} data-testid={`action-badge-${action}`}>
        {action.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-slate-900" data-testid="activity-logs-title">Activity Logs</h2>
        <p className="text-slate-600">Monitor all system activities</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activities</CardTitle>
          <CardDescription>All user actions and system events</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No activity logs yet</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="activity-logs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-sm">{formatDate(log.timestamp)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{log.user_type}</p>
                          <p className="text-xs text-slate-500">{log.user_id.substring(0, 8)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell className="text-sm">{log.ip_address}</TableCell>
                      <TableCell className="text-sm text-slate-600">{log.device_info || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;